import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ContactApiService } from '@shared/api/api-loop/services';
import { ContactBase } from '@shared/api/api-loop/models/contact-base';
import { User } from '@shared/api/api-loop/models/user';
import { SearchType } from '@shared/modules/conversations/components/middle-view/conversations-smart-search/smart-conversation-search.base';
import { take } from 'rxjs/operators';

/**
 * Opens the participant-activity search (`/search/participants`) for a given contact. The page reads
 * the contact from the `participantId` query param (see {@link SearchIndexComponent}) and lists every
 * conversation involving them. Root-injectable so the "Search emails" action can be triggered from
 * anywhere — the hover contact card, the contacts side panel and the mailto right-click menu.
 */
@Injectable({ providedIn: 'root' })
export class ParticipantSearchNavigationService {
  private readonly router: Router = inject(Router);
  private readonly contactApiService: ContactApiService = inject(ContactApiService);

  /** Opens the participant search for a contact we already have resolved (e.g. a contact card). */
  openForContact(contact: ContactBase): void {
    if (!contact?.id) {
      return;
    }
    this.router.navigate(['/search', SearchType.participants], {
      queryParams: { participantId: contact.id }
    });
  }

  /**
   * Resolves an email address (e.g. from a `mailto:` link) to a contact, then opens the participant
   * search for it. No-ops on an empty address or when no contact matches the address exactly.
   */
  openForEmailAddress(email: string): void {
    const normalizedEmail = email?.trim().toLowerCase();
    if (!normalizedEmail) {
      return;
    }
    this.contactApiService
      .Contact_GetList({ searchQuery: normalizedEmail, size: 5 })
      .pipe(take(1))
      .subscribe(response => {
        const match = (response.resources ?? []).find(
          contact => (contact as User).email?.trim().toLowerCase() === normalizedEmail
        );
        if (match) {
          this.openForContact(match);
        }
      });
  }
}
